import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, FileText, RefreshCw, Trash2, Monitor, Megaphone, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [tab, setTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [partners, setPartners] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [funnelBorrowers, setFunnelBorrowers] = useState([]);
  const [funnelPartners, setFunnelPartners] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');

  const fetchData = useCallback(async () => {
    if (!token) { navigate('/admin'); return; }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [appsRes, partnersRes, inquiriesRes, fbRes, fpRes, statsRes] = await Promise.all([
        fetch(`${API}/applications`, { headers }),
        fetch(`${API}/partners`, { headers }),
        fetch(`${API}/system-inquiries`, { headers }),
        fetch(`${API}/funnel/borrower`, { headers }),
        fetch(`${API}/funnel/partner`, { headers }),
        fetch(`${API}/admin/stats`, { headers }),
      ]);
      if (appsRes.status === 401) { sessionStorage.removeItem('admin_token'); navigate('/admin'); return; }
      const appsData = await appsRes.json();
      const partnersData = await partnersRes.json();
      const inquiriesData = await inquiriesRes.json();
      const fbData = await fbRes.json();
      const fpData = await fpRes.json();
      const statsData = await statsRes.json();
      setApplications(appsData.applications || []);
      setPartners(partnersData.partners || []);
      setInquiries(inquiriesData.inquiries || []);
      setFunnelBorrowers(fbData.leads || []);
      setFunnelPartners(fpData.leads || []);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      const res = await fetch(`${API}/${type}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchData();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const StatusBadge = ({ status }) => {
    const classes = {
      new: 'badge-new',
      reviewed: 'badge-reviewed',
      contacted: 'badge-contacted',
    };
    return <span className={classes[status] || 'badge-new'}>{status}</span>;
  };

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Quidity Admin</span>
            <button
              data-testid="admin-refresh"
              onClick={fetchData}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Loan Apps', value: stats.total_applications, newCount: stats.new_applications, icon: <FileText className="h-5 w-5" /> },
              { label: 'Partners', value: stats.total_partners, newCount: stats.new_partners, icon: <Users className="h-5 w-5" /> },
              { label: 'System Inquiries', value: stats.total_inquiries, newCount: stats.new_inquiries, icon: <Monitor className="h-5 w-5" /> },
              { label: 'Ad Borrowers', value: stats.total_funnel_borrowers, newCount: stats.new_funnel_borrowers, icon: <Megaphone className="h-5 w-5" /> },
              { label: 'Ad Partners', value: stats.total_funnel_partners, newCount: stats.new_funnel_partners, icon: <UserPlus className="h-5 w-5" /> },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-slate-400">{s.icon}</div>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{s.label}</span>
                </div>
                <div className="flex items-end gap-2">
                  <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                  {s.newCount > 0 && <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full mb-1">{s.newCount} new</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-lg p-1 w-fit">
          <button
            data-testid="tab-applications"
            onClick={() => setTab('applications')}
            className={`text-sm font-semibold px-5 py-2.5 rounded-md transition-colors ${
              tab === 'applications' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Loan Applications ({applications.length})
          </button>
          <button
            data-testid="tab-partners"
            onClick={() => setTab('partners')}
            className={`text-sm font-semibold px-5 py-2.5 rounded-md transition-colors ${
              tab === 'partners' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Partner Applications ({partners.length})
          </button>
          <button
            data-testid="tab-inquiries"
            onClick={() => setTab('inquiries')}
            className={`text-sm font-semibold px-5 py-2.5 rounded-md transition-colors ${
              tab === 'inquiries' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            System Inquiries ({inquiries.length})
          </button>
          <button
            data-testid="tab-funnel-borrowers"
            onClick={() => setTab('funnel-borrowers')}
            className={`text-sm font-semibold px-5 py-2.5 rounded-md transition-colors ${
              tab === 'funnel-borrowers' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Ad Borrowers ({funnelBorrowers.length})
          </button>
          <button
            data-testid="tab-funnel-partners"
            onClick={() => setTab('funnel-partners')}
            className={`text-sm font-semibold px-5 py-2.5 rounded-md transition-colors ${
              tab === 'funnel-partners' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            Ad Partners ({funnelPartners.length})
          </button>
        </div>

        {/* Applications Table */}
        {tab === 'applications' && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {applications.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">No loan applications yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-slate-50">
                      <th>Name</th>
                      <th>Business</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Revenue</th>
                      <th>Amount</th>
                      <th>Contact Pref.</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id} data-testid={`app-row-${app.id}`}>
                        <td className="font-semibold text-slate-900">{app.full_name}</td>
                        <td>{app.business_name}</td>
                        <td><a href={`mailto:${app.email}`} className="text-[#115E59] hover:underline">{app.email}</a></td>
                        <td>{app.phone}</td>
                        <td>{app.monthly_revenue || 'N/A'}</td>
                        <td>{app.loan_amount || 'N/A'}</td>
                        <td className="text-xs">{app.contact_method} / {app.contact_time}</td>
                        <td><StatusBadge status={app.status} /></td>
                        <td className="text-xs text-slate-500">{formatDate(app.created_at)}</td>
                        <td>
                          <button
                            data-testid={`delete-app-${app.id}`}
                            onClick={() => handleDelete('applications', app.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Partners Table */}
        {tab === 'partners' && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {partners.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">No partner applications yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-slate-50">
                      <th>Name</th>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => (
                      <tr key={p.id} data-testid={`partner-row-${p.id}`}>
                        <td className="font-semibold text-slate-900">{p.full_name}</td>
                        <td>{p.company || 'N/A'}</td>
                        <td><a href={`mailto:${p.email}`} className="text-[#115E59] hover:underline">{p.email}</a></td>
                        <td>{p.phone}</td>
                        <td className="max-w-sm text-xs whitespace-normal break-words">{p.message || 'N/A'}</td>
                        <td><StatusBadge status={p.status} /></td>
                        <td className="text-xs text-slate-500">{formatDate(p.created_at)}</td>
                        <td>
                          <button
                            data-testid={`delete-partner-${p.id}`}
                            onClick={() => handleDelete('partners', p.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* System Inquiries Table */}
        {tab === 'inquiries' && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            {inquiries.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">No system inquiries yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-slate-50">
                      <th>Name</th>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>System Interest</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inq) => (
                      <tr key={inq.id} data-testid={`inquiry-row-${inq.id}`}>
                        <td className="font-semibold text-slate-900">{inq.full_name}</td>
                        <td>{inq.company || 'N/A'}</td>
                        <td><a href={`mailto:${inq.email}`} className="text-[#115E59] hover:underline">{inq.email}</a></td>
                        <td>{inq.phone}</td>
                        <td className="text-xs">{inq.system_interest || 'N/A'}</td>
                        <td className="max-w-sm text-xs whitespace-normal break-words">{inq.message || 'N/A'}</td>
                        <td><StatusBadge status={inq.status} /></td>
                        <td className="text-xs text-slate-500">{formatDate(inq.created_at)}</td>
                        <td>
                          <button
                            data-testid={`delete-inquiry-${inq.id}`}
                            onClick={() => handleDelete('system-inquiries', inq.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Funnel Borrowers Table */}
        {tab === 'funnel-borrowers' && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Meta Ad Funnel: Borrower Leads</span>
            </div>
            {funnelBorrowers.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">No borrower funnel leads yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-slate-50">
                      <th>Name</th>
                      <th>Business</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Goal</th>
                      <th>Amount</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnelBorrowers.map((lead) => (
                      <tr key={lead.id} data-testid={`fb-row-${lead.id}`}>
                        <td className="font-semibold text-slate-900">{lead.full_name}</td>
                        <td>{lead.business_name}</td>
                        <td><a href={`mailto:${lead.email}`} className="text-[#115E59] hover:underline">{lead.email}</a></td>
                        <td>{lead.phone}</td>
                        <td className="text-xs capitalize">{(lead.goal || '').replace(/_/g, ' ')}</td>
                        <td className="text-xs">{lead.estimated_amount || 'N/A'}</td>
                        <td className="max-w-sm text-xs whitespace-normal break-words">{lead.message || 'N/A'}</td>
                        <td><StatusBadge status={lead.status} /></td>
                        <td className="text-xs text-slate-500">{formatDate(lead.created_at)}</td>
                        <td>
                          <button
                            data-testid={`delete-fb-${lead.id}`}
                            onClick={() => handleDelete('funnel/borrower', lead.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Funnel Partners Table */}
        {tab === 'funnel-partners' && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Meta Ad Funnel: Partner Leads</span>
            </div>
            {funnelPartners.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">No partner funnel leads yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full admin-table">
                  <thead>
                    <tr className="bg-slate-50">
                      <th>Name</th>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Profession</th>
                      <th>Interest</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnelPartners.map((lead) => (
                      <tr key={lead.id} data-testid={`fp-row-${lead.id}`}>
                        <td className="font-semibold text-slate-900">{lead.full_name}</td>
                        <td>{lead.company || 'N/A'}</td>
                        <td><a href={`mailto:${lead.email}`} className="text-[#115E59] hover:underline">{lead.email}</a></td>
                        <td>{lead.phone}</td>
                        <td className="text-xs capitalize">{(lead.profession || '').replace(/_/g, ' ')}</td>
                        <td className="max-w-sm text-xs whitespace-normal break-words">{lead.interest || 'N/A'}</td>
                        <td className="max-w-sm text-xs whitespace-normal break-words">{lead.message || 'N/A'}</td>
                        <td><StatusBadge status={lead.status} /></td>
                        <td className="text-xs text-slate-500">{formatDate(lead.created_at)}</td>
                        <td>
                          <button
                            data-testid={`delete-fp-${lead.id}`}
                            onClick={() => handleDelete('funnel/partner', lead.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
